import {
  UNITS,
  customRepairLine,
  estimateLineDefaults,
  estimateLineScope,
  estimateProducts,
  formatCents,
  rateFromTotal,
  rateTimesQty,
  scopeTeaching,
  subCents,
  toCents,
  toDecimal,
  toUnitRate,
  type EstimateItem,
  type Product,
  type RoofLayouts,
} from "@roof-hawk/core";
import { useState } from "react";

import { t } from "../../i18n";
import { updateRow } from "../../lib/queries";
import { GrowBox, Modal } from "../../components/ui";
import { ProductSelect } from "./EstimateCard";
import { ScopeNoteAssist } from "./ScopeNoteAssist";

/**
 * One line of the proposal, as the customer will read it.
 *
 * This is the only screen where what the customer sees is written. The **name**
 * is the heading on their proposal and the **note** is the scope underneath it
 * — what comes off, what goes on, what is included and what costs extra. Both
 * are free text on purpose: no two roofs are sold with the same words.
 *
 * Everything else on this form is ours. Quantity and unit price work out the
 * customer price, but the price can also be typed straight in, because a job is
 * usually sold at a number rather than a rate — and then the unit price follows
 * from it rather than the other way round.
 *
 * **It saves as you go.** Jonathon, 2026-08-19: "everything needs to auto
 * save." A box is written when you leave it and a dropdown when you change it —
 * never on every keystroke, which was the earlier version's mistake and is
 * still not what this does. So there is no Save button: a half-typed scope is
 * saved as a half-typed scope, which is what somebody who has just been
 * interrupted actually wants.
 *
 * Quantity, unit price and customer price move together, so leaving any one of
 * them writes all three. Typing a total and closing the form must not leave the
 * rate saying something else.
 */
export function EstimateLineModal({
  item,
  products,
  trades,
  measurements,
  onClose,
  onSave,
}: {
  item: EstimateItem;
  products: readonly Product[];
  trades: readonly string[];
  /** The job's roofs, so a measured product can work its own quantity out. */
  measurements?: RoofLayouts | null;
  onClose: () => void;
  onSave: (patch: Record<string, unknown>) => Promise<void>;
}) {
  const [productId, setProductId] = useState(item.product_id);
  const [trade, setTrade] = useState(item.trade ?? "");
  const [color, setColor] = useState(item.color ?? "");
  const [name, setName] = useState(item.description ?? "");
  const [qty, setQty] = useState(String(item.qty ?? ""));
  const [unit, setUnit] = useState(item.unit ?? "");
  const [unitPrice, setUnitPrice] = useState(
    item.unit_price == null ? "" : String(item.unit_price),
  );
  const [price, setPrice] = useState(String(item.customer_price ?? "0"));
  const [discount, setDiscount] = useState(String(item.discount ?? "0"));
  const [note, setNote] = useState(item.note ?? "");
  const [busy, setBusy] = useState(false);
  const [savedOnce, setSavedOnce] = useState(false);
  /** Why the quantity says what it says, when a product worked it out. */
  const [basis, setBasis] = useState<string | null>(null);
  /** Set by choosing Custom Repair, so the click visibly does something. */
  const [repair, setRepair] = useState(false);
  /** Set once the wording on this line has been pushed back to the product. */
  const [taught, setTaught] = useState(false);
  /**
   * Its own flag rather than the form's `busy`. Leaving the Note box is what
   * saves the line, and it is also how you get to this button — sharing one
   * flag meant the blur disabled the button under the click that caused it.
   */
  const [teaching, setTeaching] = useState(false);
  /** Why the last attempt to teach the product failed, in words. */
  const [teachError, setTeachError] = useState<string | null>(null);
  /**
   * The protective clauses on this line (ADR 0100). Held here only so the
   * teach button can see them: they arrive with a draft and are saved with it.
   */
  const [qualifiers, setQualifiers] = useState<readonly string[]>(
    item.scope_qualifiers ?? [],
  );

  /**
   * One field, written on its own, as soon as it is left.
   *
   * No comparison against what was there before: this form's boxes are left
   * once each, and a write that changes nothing costs one round trip and
   * nothing else. The three money boxes are the reason — they change each
   * other, so "has this box changed" is the wrong question to ask of any one
   * of them.
   */
  async function save(patch: Record<string, unknown>) {
    setBusy(true);
    try {
      await onSave(patch);
      setSavedOnce(true);
    } finally {
      setBusy(false);
    }
  }

  /** The three that move together. Money goes through core on the way out. */
  const saveMoney = (over: Partial<Record<string, string>> = {}) => {
    const q = num(over.qty ?? qty);
    const rate = over.unitPrice ?? unitPrice;
    const total = over.price ?? price;
    return save({
      qty: q,
      unit_price: rate === "" ? null : toUnitRate(rate),
      customer_price: toDecimal(toCents(total)),
    });
  };

  const product = products.find((p) => p.id === productId);
  const colours = product?.color_options ?? null;

  /**
   * The catalogue, cut down to what a customer can be shown.
   *
   * Jonathon, 2026-09-04: "i only want products available to select on the
   * estimate line item section if they have notes (scope of work)." The parts
   * — nails, sealant, a single panel — are still every bit as real, and the
   * material order, the work order and the other expenses still offer all 634
   * of them. This screen is the one a homeowner ends up reading, so it offers
   * what has something to say. Anything else goes on as a Custom Repair.
   */
  const offered = estimateProducts(products as Product[], productId);

  /** What the product writes onto a line today, so the button knows if it differs. */
  const savedScope = product ? estimateLineScope(product) : null;

  /** Whether the button is offered, refused or neither — decided in core. */
  const teach = product
    ? scopeTeaching({
        productName: product.name,
        productScope: product.customer_scope,
        note,
        qualifiers,
      })
    : null;

  /**
   * Push this line's wording back onto the product it came from.
   *
   * Deliberately only the scope. The heading, the unit and the price are all
   * things a rep tunes per job — "Gutters — front elevation only" is right for
   * this house and wrong for the catalogue — while the scope is the part that
   * is worth the same on every roof. Nothing else on the product is touched
   * and no other estimate is rewritten: a line already sold keeps its words.
   *
   * It asks first, naming the product: one click rewrites every future line.
   * A failed write is said on screen rather than thrown into nowhere.
   */
  async function teachProduct() {
    if (!product || !teach?.ok) return;
    if (!window.confirm(teach.confirm)) return;
    setTeaching(true);
    setTeachError(null);
    try {
      await updateRow("products", product.id, { customer_scope: teach.scope });
      setTaught(true);
    } catch (cause) {
      setTeachError(
        t("Could not save the wording to {product}: {error}", {
          product: product.name,
          error: cause instanceof Error ? cause.message : String(cause),
        }),
      );
    } finally {
      setTeaching(false);
    }
  }

  const num = (v: string) => {
    const n = Number(v.trim());
    return Number.isFinite(n) ? n : 0;
  };

  /** Typing a rate multiplies it out. */
  const setRate = (value: string) => {
    setUnitPrice(value);
    const q = num(qty);
    if (q > 0) setPrice(String(toDecimal(rateTimesQty(toUnitRate(value), q))));
  };

  /** Typing a total divides it back. A job is sold at a number. */
  const setTotal = (value: string) => {
    setPrice(value);
    const q = num(qty);
    if (q > 0) setUnitPrice(String(rateFromTotal(toCents(value), q)));
  };

  const setQuantity = (value: string) => {
    setQty(value);
    const rate = toUnitRate(unitPrice);
    if (rate > 0) setPrice(String(toDecimal(rateTimesQty(rate, num(value)))));
  };

  /**
   * A one-off repair: no product, and everything on the form is yours to type.
   *
   * Nothing already on the line is thrown away except the colour, which
   * belonged to a product that is no longer behind this row. The unit becomes
   * each and the quantity one, because a repair is sold at a number rather than
   * a rate — type $850 into Customer Price and that is the price.
   */
  const chooseRepair = () => {
    const patch = customRepairLine();
    setRepair(true);
    setProductId(null);
    setColor("");
    setUnit(patch.unit);
    setQty(String(patch.qty));
    // The rate was per foot or per square of the product that is gone; one
    // "each" at that rate would not be the price on the line. The price stays.
    setUnitPrice("");
    setBasis(null);
    void save(patch);
  };

  return (
    <Modal
      title={t("Update Line Item")}
      subtitle={t(
        "Saves as you go. The name and the scope are what the customer reads on the proposal.",
      )}
      onClose={onClose}
      actions={
        <button className="btn-primary" onClick={onClose}>
          {t("Done")}
        </button>
      }
    >
      <div className="fgrid one">
        <div className="field">
          <label>{t("Product")}</label>
          {/* The repair is offered above the catalogue rather than inside it,
              because "leave the product blank and type everything" is not
              something anybody guesses. Jonathon, 2026-09-01: "a custom repair
              option that shows at the top when we click on line item ... where
              we can just enter in everything manually, select the trade etc."
              It is also the first entry in the picker's own list, for the rep
              who has already clicked into the box. */}
          <div className="repairpick">
            <button
              type="button"
              className={`btn-small${repair ? " on" : ""}`}
              onClick={chooseRepair}
            >
              {t("Custom Repair")}
            </button>
            <span className="na-hint">
              {t(
                "One-off work that is not in the catalogue — type the name, the trade, the scope and the price yourself.",
              )}
            </span>
          </div>
          {/* Why the list is short. A rep who knows the catalogue has 600-odd
              products in it will otherwise read the missing ones as a bug. */}
          <div className="na-hint">
            {t(
              "The picker offers the products that carry a scope of work, because this is what the customer reads. Everything else is still on the material order, the work order and the other expenses.",
            )}
          </div>
          <ProductSelect
            products={offered}
            value={productId}
            width={420}
            onCustom={chooseRepair}
            onChange={(p) => {
              setProductId(p?.id ?? null);
              if (!p) {
                void save({ product_id: null });
                return;
              }
              setRepair(false);
              // Filling the form in is a convenience, not a rule: the
              // customer's heading is often not the catalogue name, so it stays
              // editable and an edited one is never overwritten. What it fills
              // in is the product's customer wording when it has any, and the
              // catalogue label when it does not.
              const defaults = estimateLineDefaults(p, measurements);
              // A line nobody has priced yet is a line nobody has set up, and
              // the "SQ" on it is the default every new row is inserted with
              // rather than an answer somebody gave. Gutters are sold by the
              // foot, so taking the product's unit is what keeps 333 from
              // meaning 333 squares. A priced line keeps whatever it has.
              const fresh = toCents(price) === 0;
              const filled = name.trim() ? name : defaults.description;
              const withUnit = fresh || !unit ? (defaults.unit ?? unit) : unit;
              const withNote = note.trim() ? note : (defaults.note ?? "");
              setName(filled);
              setUnit(withUnit);
              setNote(withNote);
              const patch: Record<string, unknown> = {
                product_id: p.id,
                description: filled || null,
                unit: withUnit || null,
                note: withNote || null,
              };
              /*
               * The quantity and the rate, but only onto a line nobody has
               * priced yet. A product knows what it is measured against and the
               * job has a roof, so 333 feet of gutter is worked out rather than
               * counted on a phone — and it lands in an editable box, which is
               * the whole point of it being a suggestion. A line already
               * carrying a price is one somebody has settled, and swapping the
               * product on it must not quietly re-price the job.
               */
              if (fresh && defaults.qty != null) {
                setQty(String(defaults.qty));
                setBasis(defaults.basis);
                patch.qty = defaults.qty;
                if (defaults.unitPrice != null) {
                  setUnitPrice(String(defaults.unitPrice));
                  patch.unit_price = defaults.unitPrice;
                }
                if (defaults.customerPrice != null) {
                  setPrice(String(defaults.customerPrice));
                  patch.customer_price = defaults.customerPrice;
                }
              }
              void save(patch);
            }}
          />
          {basis && (
            <span className="na-hint">
              {t(
                "{basis} — off this job's roof. Change the quantity if you counted it yourself.",
                { basis },
              )}
            </span>
          )}
        </div>

        {colours && colours.length > 0 && (
          <div className="field">
            <label>{t("Color")}</label>
            <select
              value={color}
              onChange={(e) => {
                setColor(e.target.value);
                void save({ color: e.target.value || null });
              }}
            >
              <option value="">{t("Select Color")}</option>
              {colours.map((c) => (
                <option key={c} value={c}>
                  {c}
                </option>
              ))}
            </select>
          </div>
        )}

        <div className="field">
          <label>{t("Trade")}</label>
          <select
            value={trade}
            onChange={(e) => {
              setTrade(e.target.value);
              void save({ trade: e.target.value || null });
            }}
          >
            <option value="">—</option>
            {trades.map((name) => (
              <option key={name} value={name}>
                {name}
              </option>
            ))}
          </select>
        </div>

        <div className="field">
          <label>{t("Name")}</label>
          <input
            placeholder={
              repair
                ? t(
                    "The repair, as the customer reads it — “Chimney flashing rebuilt”",
                  )
                : t("What the customer sees as the heading")
            }
            value={name}
            onChange={(e) => setName(e.target.value)}
            onBlur={() => void save({ description: name || null })}
          />
          {repair && (
            <span className="na-hint">
              {t(
                "Custom repair — no product behind this line. Pick the trade above and put the price in Customer Price.",
              )}
            </span>
          )}
        </div>

        <div className="field">
          <label>{t("Quantity")}</label>
          <input
            inputMode="decimal"
            value={qty}
            onChange={(e) => setQuantity(e.target.value)}
            onBlur={() => void saveMoney()}
          />
        </div>

        <div className="field">
          <label>{t("Unit")}</label>
          <select
            value={unit}
            onChange={(e) => {
              setUnit(e.target.value);
              void save({ unit: e.target.value || null });
            }}
          >
            <option value="">—</option>
            {UNITS.map((u) => (
              <option key={u} value={u}>
                {u}
              </option>
            ))}
          </select>
        </div>

        <div className="field">
          <label>{t("Unit Price")}</label>
          <input
            inputMode="decimal"
            value={unitPrice}
            onChange={(e) => setRate(e.target.value)}
            onBlur={() => void saveMoney()}
          />
        </div>

        <div className="field">
          <label>{t("Discount")}</label>
          <input
            inputMode="decimal"
            value={discount}
            onChange={(e) => setDiscount(e.target.value)}
            onBlur={() => void save({ discount: toDecimal(toCents(discount)) })}
          />
        </div>

        <div className="field">
          <label>{t("Customer Price")}</label>
          <input
            inputMode="decimal"
            value={price}
            onChange={(e) => setTotal(e.target.value)}
            onBlur={() => void saveMoney()}
          />
          <span className="na-hint">
            {t(
              "{amount} after discount. Type here to sell at a number and the unit price follows.",
              {
                amount: formatCents(
                  subCents(toCents(price), toCents(discount)),
                ),
              },
            )}
          </span>
        </div>

        <div className="na-hint" role="status">
          {busy
            ? t("Saving…")
            : savedOnce
              ? t("Saved")
              : t("Changes save on their own.")}
        </div>

        <div className="field">
          <label>{t("Note")}</label>
          {/* Fourteen lines to start with, and taller the moment the scope
              needs it — a rep opening a scope they wrote last week sees all of
              it rather than a window onto the middle of it. The corner drags
              like any other box. */}
          <GrowBox
            rows={14}
            placeholder={t("The scope of work, as the customer reads it.")}
            value={note}
            onChange={(e) => {
              setNote(e.target.value);
              setTaught(false);
            }}
            onBlur={() => void save({ note: note || null })}
          />
          {/* Speak the work and AI drafts this scope. The draft is saved the
              moment it lands — this editor saves as you go, and a drafted
              scope must not evaporate because nobody clicked out of the box.
              Every edit after that saves the way the note always has. */}
          <ScopeNoteAssist
            lineName={name.trim() || null}
            trade={trade || null}
            onDraft={(draft, qualifiers) => {
              setNote(draft);
              // The clauses ride along with the note they were chosen for, in
              // the same save — a scope stored without them is a scope with
              // its protection quietly missing (ADR 0100).
              setTaught(false);
              setQualifiers(qualifiers);
              void save({ note: draft, scope_qualifiers: qualifiers });
            }}
          />
          {/* Teaching the catalogue from the job in front of you.
              Jonathon, 2026-09-04: "there also needs to be a feature where they
              can click on a button to update the template ... on the estimate
              building screen where they're editing it and in the product page."
              A rep who has just improved the wording on a real proposal is the
              person best placed to improve it for everybody, and the
              alternative is that the better sentence lives on one job and
              nowhere else.

              Offered only when the wording actually differs from what the
              product already says, so the button is never a no-op — and never
              on a Custom Repair, which has no product to teach. Shown but
              refused when the scope carries protective clauses the product
              cannot hold, so the rep learns why rather than wondering. */}
          {product && teach && !taught && (
            <div className="teachpick">
              <button
                type="button"
                className="btn-small"
                disabled={teaching || !teach.ok}
                onClick={() => void teachProduct()}
              >
                {t("Save this wording to {product}", { product: product.name })}
              </button>
              <span className="na-hint">
                {!teach.ok
                  ? t(teach.reason)
                  : savedScope
                    ? t(
                        "Replaces what this product writes onto every future line.",
                      )
                    : t(
                        "This product has no wording yet. Saving it puts this scope on every future line.",
                      )}
              </span>
            </div>
          )}
          {teachError && (
            <div className="err" role="alert">
              {teachError}
            </div>
          )}
          {taught && (
            <span className="na-hint">
              {t(
                "Saved to the product. Lines already on other estimates are left as they are.",
              )}
            </span>
          )}
        </div>
      </div>
    </Modal>
  );
}
